import { useId, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import config from 'src/config'
import {
  ManufacturerGuaranteeSnapshot,
  ProductGuaranteeInfo,
  statutoryGuaranteeRightsText,
} from 'src/shared/manufacturerGuarantees'

const GuaranteeLabel: React.FC<{ item: ProductGuaranteeInfo; guarantee: ManufacturerGuaranteeSnapshot }> = ({ item, guarantee }) => {
  const [open, setOpen] = useState(false)
  const titleId = useId()
  if (!guarantee.label) return null
  const query = new URLSearchParams({
    shop: String(config.shop.shop), id: String(item.id), key: item.key, program: guarantee.programId,
    revision: guarantee.revision,
  })
  const src = `${config.api_server.online}/products/guarantees/label?${query}`
  const labelDescription = `EU-GARAN: ${guarantee.label.years} Jahre kostenlose Haltbarkeitsgarantie von ${guarantee.label.producer} für Modell ${guarantee.label.model}`

  return <Box component='figure' sx={{ m: 0, maxWidth: 320 }}>
    <Link href={src} target='_blank' rel='noopener' aria-haspopup='dialog' onClick={event => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      event.preventDefault()
      setOpen(true)
    }} sx={{ display: 'block' }}>
      <Box component='img' src={src} alt={labelDescription} sx={{ display: 'block', width: '100%', height: 'auto' }} />
    </Link>
    <Typography component='figcaption' variant='body2' sx={{ mt: 1 }}>
      Modell {guarantee.label.model}. Zum Vergrößern das Label öffnen.
    </Typography>
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth='md' fullWidth aria-labelledby={titleId}
      sx={{ '& .MuiDialog-paper': { m: { xs: 1, sm: 4 }, width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' } } }}>
      <DialogTitle id={titleId}>Herstellergarantie – {item.name}</DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 1, sm: 3 } }}>
        <Box component='img' src={src} alt={labelDescription} sx={{ display: 'block', width: '100%', maxWidth: 760, height: 'auto', mx: 'auto' }} />
      </DialogContent>
      <DialogActions>
        <Button component='a' href={src} target='_blank' rel='noopener'>Original öffnen</Button>
        <Button onClick={() => setOpen(false)}>Schließen</Button>
      </DialogActions>
    </Dialog>
  </Box>
}

export const ManufacturerGuaranteeLabels: React.FC<{ item?: ProductGuaranteeInfo }> = ({ item }) => {
  const guarantees = item?.guarantees.filter(guarantee => guarantee.label) || []
  if (!item || !guarantees.length) return null
  return <Box component='aside' aria-label='Herstellergarantie' sx={{ mt: 2, mb: 2 }}>
    <Typography component='h2' variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>Herstellergarantie</Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      {guarantees.map(guarantee => <GuaranteeLabel key={guarantee.programId} {...{ item, guarantee }} />)}
    </Box>
  </Box>
}

export const ManufacturerGuaranteeDetails: React.FC<{ guarantees: ManufacturerGuaranteeSnapshot[] }> = ({ guarantees }) => {
  if (!guarantees.length) return null
  return <Box>
    {guarantees.map(guarantee => <Box key={`${guarantee.manufacturerId}:${guarantee.programId}`} component='section' sx={{ mb: 3 }}>
      <Typography component='h3' variant='h6'>{guarantee.title}</Typography>
      {guarantee.scope === 'limited' && <Typography variant='body2' sx={{ mt: 1, mb: 1 }}>
        Allgemeine Herstellerbedingungen für den unten genannten Geltungsbereich. Eine Garantie besteht ausschließlich für die dort bezeichneten Waren oder Bestandteile.
      </Typography>}
      <Box component='dl' sx={{ '& dt': { fontWeight: 600, mt: 1.5 }, '& dd': { m: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } }}>
        <dt>Geltungsbereich</dt><dd>{guarantee.scopeDescription}</dd>
        <dt>Dauer und Beginn</dt><dd>{guarantee.durationText}</dd>
        <dt>Garantiegeber</dt><dd>{guarantee.guarantorName}{'\n'}{guarantee.guarantorAddress}</dd>
        <dt>Räumlicher Geltungsbereich</dt><dd>{guarantee.territory}</dd>
        <dt>Garantie in Anspruch nehmen</dt><dd>{guarantee.claimProcedure}</dd>
      </Box>
      <Typography component='h4' variant='subtitle1' sx={{ fontWeight: 600, mt: 2 }}>Garantiebedingungen</Typography>
      <Typography component='div' sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 1 }}>{guarantee.conditionsText}</Typography>
      {guarantee.conditionsUrl && <Link href={guarantee.conditionsUrl} target='_blank' rel='noopener' underline='always' sx={{ display: 'inline-block', mt: 1.5 }}>
        Originalbedingungen des Herstellers öffnen
      </Link>}
    </Box>)}
    <Typography variant='body2'>{statutoryGuaranteeRightsText}</Typography>
  </Box>
}

export const CheckoutManufacturerGuarantees: React.FC<{ items: ProductGuaranteeInfo[] }> = ({ items }) => {
  const coveredItems = items.filter(item => item.guarantees.length)
  if (!coveredItems.length) return null
  return <Box component='section' aria-label='Herstellergarantien zu Ihrer Bestellung' sx={{ mt: 3, mb: 2 }}>
    <Typography component='h2' variant='h6' sx={{ mb: 2 }}>Herstellergarantien zu Ihrer Bestellung</Typography>
    {coveredItems.map(item => <Box key={`${item.id}:${item.key}`} sx={{ mb: 3 }}>
      <Typography component='h3' variant='subtitle1' sx={{ fontWeight: 600 }}>{item.name}</Typography>
      <ManufacturerGuaranteeLabels item={item} />
      <Box component='details' sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 1, '& > summary': { cursor: 'pointer', minHeight: 28 } }}>
        <summary>Garantiebedingungen und Geltungsbereiche ansehen</summary>
        <Box sx={{ mt: 2 }}><ManufacturerGuaranteeDetails guarantees={item.guarantees} /></Box>
      </Box>
    </Box>)}
  </Box>
}
